import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { Select, Slider, Switch } from "antd";
import { Input } from "@vds/inputs";
import { updateUI, updateAttrSchedule } from "../../../../store/actions/deviceProfile";
import ATSToolTip from "../../../../global/components/ATSToolTip";
import CircleIIcon from "../../../../assets/circle_i.svg";
import TooltipIIcon from "../../../../assets/TooltipIcon.svg";
import { RadioButtonGroup } from "@vds/radio-buttons";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import ScheduleTimeFrame from "../../../../global/components/ScheduleTimeFrame";

function AttributeFormGroup({ title, stateSelector, isMinimumVisible, expandedSection, setExpandedSection }) {
  const sliderRef = useRef(null);
  const [isInView, setIsInView] = useState(true);
  const [localAlertValue, setLocalAlertValue] = useState("no alert");
  const [isTooltipVisible, setIsTooltipVisible] = useState(true);
  const dispatch = useDispatch();
  const { formFields, alertGroupOptions,selectedDeviceProfile } = useSelector((state) => ({
    formFields: state.deviceProfile.ui.form.fields,
    alertGroupOptions: state.deviceProfile.ui.form.alertGroups,
    selectedDeviceProfile: Array.isArray(
      state.deviceProfile.ui.form.deviceTypes
    )
      ? state.deviceProfile.ui.form.deviceTypes.filter(
          (deviceType) =>
            deviceType.value ===
            state.deviceProfile.ui.form.fields.step1.deviceType.value
        )
      : [],
  }));
  const formState = formFields?.step2?.[stateSelector];
  const alertValue = formState?.alert?.value ?? "no alert";
  const isAlertEnabled = alertValue !== "no alert";
  const isExpanded = expandedSection === stateSelector;
  const isSwitchEnabled = isExpanded || alertValue !== "no alert";
  const deviceModelVal = selectedDeviceProfile[0]?.devicemodel;

  useEffect(() => {
  if (sliderRef.current) {
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      setIsInView(entry.isIntersecting);
    }, { threshold: 0.1 });
    observer.observe(sliderRef.current);
    let scrollTimeout;
    const handleScroll = () => {
      setIsTooltipVisible(false);
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (isInView && isSwitchEnabled && isExpanded) {
          setIsTooltipVisible(true);
        }
      }, 200);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }
}, [isInView, isSwitchEnabled, isExpanded]);


  useEffect(() => {
    setLocalAlertValue(alertValue);
  }, [alertValue]);

  const resetToDefaultValues = () => {
    const updated = {
      minimum: {
        ...formState.minimum,
        value: formState?.minimum?.minimum,
      },
      maximum: {
        ...formState.maximum,
        value: formState?.maximum?.maximum,
      },
      alert:{
         ...formState.alert,
          value: "no alert",
      },
      alertType: {
        ...formState.alertType,
        value: "notification",
      },
      alertGroup: {
        ...formState.alertGroup,
        value: [],
      },
    };

    dispatch(updateUI({
      key: "form",
      value: {
        fields: {
          ...formFields,
          step2: {
            ...formFields.step2,
            [stateSelector]: {
              ...formState,
              ...updated,
            },
          },
        },
      },
    }));
  };

  const handleFormField = (field, value) => {
    const numericFields = ["minimum", "maximum"];
    let parsedValue = value;
    if (numericFields.includes(field) && value !== "") {
      parsedValue = !isNaN(value) && !isNaN(parseFloat(value)) ? parseFloat(value) : value;

      if (field === "minimum" && typeof parsedValue === "number") {
        const minAllowed = formState?.minimum?.minimum;
        if (parsedValue <= minAllowed) parsedValue = minAllowed;
      }

      if (field === "maximum" && typeof parsedValue === "number") {
        const maxAllowed = formState?.maximum?.maximum;
        if (parsedValue >= maxAllowed) parsedValue = maxAllowed;
      }
    }

    dispatch(updateUI({
      key: "form",
      value: {
        fields: {
          ...formFields,
          step2: {
            ...formFields.step2,
            [stateSelector]: {
              ...formState,
              [field]: {
                ...formState?.[field],
                value: value,
               // value: parsedValue,
              },
            },
          },
        },
      },
    }));
  };

  const handleFormFieldBlur = (field) => {
    const currentValue = formState?.[field]?.value;
    const absoluteMin = formState?.minimum?.minimum;
    const absoluteMax = formState?.maximum?.maximum;
    let validatedValue = currentValue;

    const isInvalid = currentValue === "" || isNaN(parseFloat(currentValue));
    const numValue = parseFloat(currentValue);
    if (field === "minimum" || field === "maximum") {
        if (isInvalid) {
            validatedValue = field === "minimum" ? absoluteMin : absoluteMax;
        } else {
      if (field === "minimum") {
        validatedValue = Math.max(numValue, absoluteMin);
        const currentMax = formState?.maximum?.value;
        if (typeof currentMax === "number") {
          validatedValue = Math.min(validatedValue, currentMax);
        }
      } else if (field === "maximum") {
        validatedValue = Math.min(numValue, absoluteMax);
        const currentMin = formState?.minimum?.value;
        if (typeof currentMin === "number") {
          validatedValue = Math.max(validatedValue, currentMin);
        }
      }
  }
    } else {
        let min, max;
        switch (field) {
            case "tiltAngle":
                min = 3;
                max = 180;
                break;
            case "shockThresh":
                min = 0.1;
                max = 10;
                break;
            case "accelThresh":
            case "stoppedThresh":
                min = 0;
                max = 16;
                break;
            case "stoppedPeriod":
                min = 1;
                max = 4320;
                break;
            default:
                return;
        }

        if (isInvalid) {
            validatedValue = min;
        } else {
            validatedValue = Math.max(min, Math.min(numValue, max));
        }
    }
    if (validatedValue !== currentValue || formState?.[field]?.class) {
      dispatch(updateUI({
        key: "form",
        value: {
          fields: {
            ...formFields,
            step2: {
              ...formFields.step2,
              [stateSelector]: {
                ...formState,
                [field]: {
                  ...formState?.[field],
                  value: validatedValue,
                    class: "",
                },
              },
            },
          },
        },
      }));
    }
  };

  const handleSliderSlide = (val) => {
    const updated = isMinimumVisible
      ? {
          minimum: {
            ...formState.minimum,
            value: val[0],
          },
          maximum: {
            ...formState.maximum,
            value: val[1],
          },
        }
      : {
          maximum: {
            ...formState.maximum,
            value: val,
          },
        };

        

    dispatch(updateUI({
      key: "form",
      value: {
        fields: {
          ...formFields,
          step2: {
            ...formFields.step2,
            [stateSelector]: {
              ...formState,
              ...updated,
            },
          },
        },
      },
    }));
  };

  const handleSingleSliderSlide = (val) => {
    const updated = isMinimumVisible
      ? {
          minimum: {
            ...formState.minimum,
            value: val[0],
          },
          maximum: {
            ...formState.maximum,
            value: val[1],
          },
        }
      : {
          maximum: {
            ...formState.maximum,
            value: val,
          },
        };

        

    dispatch(updateUI({
      key: "form",
      value: {
        fields: {
          ...formFields,
          step2: {
            ...formFields.step2,
            [stateSelector]: {
              ...formState,
              ...updated,
            },
          },
        },
      },
    }));
  };

  const handleToggle = (checked) => {
    if (!checked) {
      handleFormField("alert", "no alert");
      setLocalAlertValue("no alert");
      resetToDefaultValues();
      setExpandedSection(null);
    } else {
    if (selectedDeviceProfile[0]?.devicemodel === "BLE") {
      handleFormField("alert", "platform alert");
      setLocalAlertValue("platform alert");
    } else {
      handleFormField("alert", "device alert");
      setLocalAlertValue("device alert");
    }
      setExpandedSection(stateSelector);
    }
  };

  const handleRadioChange = (val) => {
    handleFormField("alert", val);
    setLocalAlertValue(val);
    if (val !== "no alert") {
      setExpandedSection(stateSelector);
    }
  };

  const handleArrowClick = () => {
    if (isExpanded) {
      handleFormField("alert", localAlertValue);
      setExpandedSection(null);
    } else {
      setExpandedSection(stateSelector);
    }
  };

  return (    
    <div  style={{ borderBottom: "1px solid #d9d9d9" }}>
      <div className="attribute-field d-flex justify-content-between align-items-center">
        <div className="font-face-vzw" style={{ fontSize: 18, fontWeight: 700 }}>{title}</div>
        <div className="d-flex align-items-center">
          <Switch
            checked={isSwitchEnabled}
            onChange={handleToggle}
            style={{ backgroundColor: isSwitchEnabled ? "#008331" : "#d8dada" }}
          />
          <button
            onClick={handleArrowClick}
            disabled={!isSwitchEnabled && alertValue === "no alert" && !isExpanded}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              marginLeft: "8px",
              cursor: (!isSwitchEnabled && alertValue === "no alert" && !isExpanded) ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              color: (!isSwitchEnabled && alertValue === "no alert" && !isExpanded) ? "#ccc" : "#000",
            }}
          >
            <span className="ant-select-arrow">
              {isExpanded ? (
                <UpOutlined style={{ fontSize: "14px" }} />
              ) : (
                <DownOutlined style={{ fontSize: "14px" }} />
              )}
            </span>
          </button>
        </div>
      </div>
      {isExpanded && (
        <>
         <div className="py-2 w-100 d-flex flex-row justify-content-start align-items-center">
            <div className="w-100 ats-alert-radio-button-group-container d-flex flex-row justify-content-start align-items-center">
              <RadioButtonGroup
                className="w-90 d-flex flex-row justify-content-evenly align-items-center ats-alert-radio-button-group attributes-radio-label"
                onChange={(e) => handleRadioChange(e?.target?.value)}
                error={false}
                value={localAlertValue}
                data={[
                  ...(deviceModelVal !== "BLE"
                ? [
                    {
                      name: "alert",
                      label: "Device alert",
                      value: "device alert",
                      ariaLabel: "Device alert",
                    },
                  ]
                : []),
             ...(deviceModelVal === "BLE" || deviceModelVal === "GAT") &&
              !(["anymotion", "shock", "tilt"].includes(stateSelector))
                ? [
                    {
                      name: "alert",
                      label: "Platform alert",
                      value: "platform alert",
                      ariaLabel: "Platform alert",
                    },
                  ]
                : [],
                ]}
              />
            </div>
          </div>
          {stateSelector!=="anymotion" && stateSelector !== "shock" && stateSelector !== "tilt" ?
          <div className="py-2 w-100 d-flex flex-row justify-content-start align-items-center">
            {isMinimumVisible && (
              <div className="single-min-max-field d-flex flex-column justify-content-center align-items-start">
                 <span
              className={
                formFields?.step2[stateSelector]?.alert?.value === "no alert"
                  ? "d-flex flex-row justify-content-start align-items-center device-profile-label ats-disabled-text attributes-label"
                  : "d-flex flex-row justify-content-start align-items-center device-profile-label attributes-label"
              }
            >
              Minimum
            </span>
                 <div className="w-100 pe-2">
                <Input
                  type="number"
                  className={`shipment-form-fields ${formFields?.step2[stateSelector]?.minimum?.class}`}
                  value={`${formState?.minimum?.value}`}

                  onChange={(e) => handleFormField("minimum", e.target.value)}
                  onBlur={() => handleFormFieldBlur("minimum")}
                  required={false}
                  disabled={!isAlertEnabled}
                />
                </div>
              </div>
            )}
            <div className="single-min-max-field d-flex flex-column justify-content-center align-items-start">
                    <span
                  className={
                    formFields?.step2[stateSelector]?.alert?.value === "no alert"
                    ? "d-flex flex-row justify-content-start align-items-center device-profile-label ats-disabled-text attributes-label"
                    : "d-flex flex-row justify-content-start align-items-center device-profile-label attributes-label"
                  }
                >
                  Maximum
                </span>
                <div className="w-100 pl-2">
              <Input
                type="number"
                className={`shipment-form-fields ${formFields?.step2[stateSelector]?.maximum?.class}`}
                value={`${formState?.maximum?.value}`}
                onChange={(e) => handleFormField("maximum", e.target.value)}
                required={false}
                onBlur={() => handleFormFieldBlur("maximum")}
                disabled={!isAlertEnabled}
              />
              </div>
            </div>
           <div
          className={
            isMinimumVisible
              ? "ms-3 d-flex flex-column justify-content-center align-items-start flex-grow-1"
              : " ms-3 d-flex flex-column justify-content-center align-items-start flex-grow-1"
          }
        >
          <div
            className={
              formFields?.step2[stateSelector]?.maximum?.maximum > 9999
                ? "w-100 device-profile-form-slider-with-extra-space"
                : "w-100 device-profile-form-slider"
            }
          >
              <div ref={sliderRef}>
                <Slider
                className="custom-slider"
                  id={stateSelector}
                  range={isMinimumVisible}
                  value={
                    isMinimumVisible
                      ? [formState?.minimum?.value, formState?.maximum?.value]
                      : formState?.maximum?.value
                  }
                  min={formState?.minimum?.minimum}
                  max={formState?.maximum?.maximum}
                  marks={{
                    0: formFields?.step2[stateSelector]?.minimum?.minimum,
                    100: formFields?.step2[stateSelector]?.maximum?.maximum,
                  }}
                  onChange={handleSliderSlide}
                  tooltip={{
                    open: isInView,
                    placement: "top",
                    autoAdjustOverflow: false,
                    formatter: (value) => value,
                  }}
                  disabled={!isAlertEnabled}
                  styles={{
                    track: { backgroundColor: "#008331" },
                    rail: { backgroundColor: "#FF8027" },
                    handle: { background: "#000000", borderColor: "#000000" },
                  }}
                />
                 </div>
              </div>
            </div>
          </div>
        : 
        <>
       {stateSelector === "tilt"?
         <div className="py-2 w-100 d-flex flex-row justify-content-start align-items-center">
            {isMinimumVisible && (
              <div className="single-max-field d-flex flex-column justify-content-center align-items-start">
                 <span
              className={
                formFields?.step2[stateSelector]?.alert?.value === "no alert"
                  ? "d-flex flex-row justify-content-start align-items-center device-profile-label ats-disabled-text attributes-label"
                  : "d-flex flex-row justify-content-start align-items-center device-profile-label attributes-label"
              }
            >
             Tilt angle
            </span>
                 <div className="w-100 pe-2">
                <Input
                  type="number"
                  className={`shipment-form-fields ${formFields?.step2[stateSelector]?.minimum?.class}`}
                   value={
                  typeof formState?.tiltAngle === "object"
                    ? formState?.tiltAngle?.value
                    : formState?.tiltAngle
                }
                  onChange={(e) => handleFormField("tiltAngle", e.target.value)}
                  onBlur={() => handleFormFieldBlur("tiltAngle")}
                  required={false}
                  disabled={!isAlertEnabled}
                />
                </div>
              </div>
            )}
           <div
          className={
            isMinimumVisible
              ? "ms-3 d-flex flex-column justify-content-center align-items-start flex-grow-1"
              : " ms-3 d-flex flex-column justify-content-center align-items-start flex-grow-1"
          }
        >
          <div
            className={
              formFields?.step2[stateSelector]?.maximum?.maximum > 9999
                ? "w-100 device-profile-form-slider-with-extra-space"
                : "w-100 device-profile-form-slider"
            }
          >
              <div ref={sliderRef}>
                  <Slider
                  className="custom-slider"
                  id={stateSelector}
                  value={
                  typeof formState?.tiltAngle === "object"
                    ? formState?.tiltAngle?.value
                    : formState?.tiltAngle
                }
                  min={3}
                  max={180}
                  step={1}
                  onChange={(val) => handleFormField("tiltAngle", val)}
                  tooltip={{
                    open: isInView,
                    placement: "top",
                    autoAdjustOverflow: false,
                    formatter: (value) => value.toFixed(1),
                  }}
                  disabled={!isAlertEnabled}
                  marks={{
                    3: "3",
                    180: "180",
                  }}
                  styles={{
                    track: { backgroundColor: "#008331" },
                    rail: { backgroundColor: "#FF8027" },
                    handle: { background: "#000000", borderColor: "#000000" },
                  }}
                />
                </div>
              </div>
            </div>
          </div> 
          :""} 
          {stateSelector === "shock"?
         <div className="py-2 w-100 d-flex flex-row justify-content-start align-items-center">
            {isMinimumVisible && (
              <div className="single-max-field d-flex flex-column justify-content-center align-items-start">
                 <span
              className={
                formFields?.step2[stateSelector]?.alert?.value === "no alert"
                  ? "d-flex flex-row justify-content-start align-items-center device-profile-label ats-disabled-text attributes-label"
                  : "d-flex flex-row justify-content-start align-items-center device-profile-label attributes-label"
              }
            >
             Shock threshold (g)
            </span>
                 <div className="w-100 pe-2">
                <Input
                  type="number"
                  className={`shipment-form-fields ${formFields?.step2[stateSelector]?.minimum?.class}`}
                   value={
                  typeof formState?.shockThresh === "object"
                    ? formState?.shockThresh?.value
                    : formState?.shockThresh
                }
                  onChange={(e) => handleFormField("shockThresh", e.target.value)}
                  onBlur={() => handleFormFieldBlur("shockThresh")}
                  required={false}
                  disabled={!isAlertEnabled}
                />
                </div>
              </div>
            )}
           <div
          className={
            isMinimumVisible
              ? "ms-3 d-flex flex-column justify-content-center align-items-start flex-grow-1"
              : " ms-3 d-flex flex-column justify-content-center align-items-start flex-grow-1"
          }
        >
          <div
            className={
              formFields?.step2[stateSelector]?.maximum?.maximum > 9999
                ? "w-100 device-profile-form-slider-with-extra-space"
                : "w-100 device-profile-form-slider"
            }
          >
              <div ref={sliderRef}>
                  <Slider
                  className="custom-slider"
                  id={stateSelector}
                  value={
                  typeof formState?.shockThresh === "object"
                    ? formState?.shockThresh?.value
                    : formState?.shockThresh
                }
                 // value={formState?.shockThresh?.value}
                  min={0.1}
                  max={10}
                  step={0.1}
                  onChange={(val) => handleFormField("shockThresh", val)}
                  tooltip={{
                    open: isInView,
                    placement: "top",
                    autoAdjustOverflow: false,
                    formatter: (value) => value.toFixed(1),
                  }}
                  disabled={!isAlertEnabled}
                  marks={{
                    0.1: "0.1",
                    10: "10",
                  }}
                  styles={{
                    track: { backgroundColor: "#008331" },
                    rail: { backgroundColor: "#FF8027" },
                    handle: { background: "#000000", borderColor: "#000000" },
                  }}
                />
                 </div>
              </div>
            </div>
          </div> 
          :""}
          
          {stateSelector === "anymotion"?
            <>
          <div className="py-2 w-100 d-flex flex-row justify-content-start align-items-center">
            {isMinimumVisible && (
              <div className="single-max-field d-flex flex-column justify-content-center align-items-start">
                 <span
              className={
                formFields?.step2[stateSelector]?.alert?.value === "no alert"
                  ? "d-flex flex-row justify-content-start align-items-center device-profile-label ats-disabled-text attributes-label"
                  : "d-flex flex-row justify-content-start align-items-center device-profile-label attributes-label"
              }
            >
              Acceleration threshold (g)
            </span>
                 <div className="w-100 pe-2">
                <Input
                  type="number"
                  className={`shipment-form-fields ${formFields?.step2[stateSelector]?.minimum?.class}`}
                  value={
                  typeof formState?.accelThresh === "object"
                    ? formState?.accelThresh?.value
                    : formState?.accelThresh
                }
                  onChange={(e) => handleFormField("accelThresh", e.target.value)}
                  required={false}
                  onBlur={() => handleFormFieldBlur("accelThresh")}
                  disabled={!isAlertEnabled}
                />
                </div>
              </div>
            )}
           <div
          className={
            isMinimumVisible
              ? "ms-3 d-flex flex-column justify-content-center align-items-start flex-grow-1"
              : " ms-3 d-flex flex-column justify-content-center align-items-start flex-grow-1"
          }
        >
          <div
            className={
              formFields?.step2[stateSelector]?.maximum?.maximum > 9999
                ? "w-100 device-profile-form-slider-with-extra-space"
                : "w-100 device-profile-form-slider"
            }
          >
              <div ref={sliderRef}>
                  <Slider
                  className="custom-slider"
                  id={stateSelector}
                   value={
                  typeof formState?.accelThresh === "object"
                    ? formState?.accelThresh?.value
                    : formState?.accelThresh
                }
                  min={0}
                  max={16}
                  step={0.1}
                  onChange={(val) => handleFormField("accelThresh", val)}
                  tooltip={{
                    open: isInView,
                    placement: "top",
                    autoAdjustOverflow: false,
                    formatter: (value) => value.toFixed(1),
                  }}
                  disabled={!isAlertEnabled}
                  marks={{
                    0: "0",
                    16: "16",
                  }}
                  styles={{
                    track: { backgroundColor: "#008331" },
                    rail: { backgroundColor: "#FF8027" },
                    handle: { background: "#000000", borderColor: "#000000" },
                  }}
                />
                 </div>
              </div>
            </div>
          </div>

            <div className="py-2 w-100 d-flex flex-row justify-content-start align-items-center">
            {isMinimumVisible && (
              <div className="single-max-field d-flex flex-column justify-content-center align-items-start">
                 <span
              className={
                formFields?.step2[stateSelector]?.alert?.value === "no alert"
                  ? "d-flex flex-row justify-content-start align-items-center device-profile-label ats-disabled-text attributes-label"
                  : "d-flex flex-row justify-content-start align-items-center device-profile-label attributes-label"
              }
            >
              Stopped threshold (g)
            </span>
                 <div className="w-100 pe-2">
                <Input
                  type="number"
                  className={`shipment-form-fields ${formFields?.step2[stateSelector]?.stoppedThresh?.class}`}
                   value={
                  typeof formState?.stoppedThresh === "object"
                    ? formState?.stoppedThresh?.value
                    : formState?.stoppedThresh
                }
                  onChange={(e) => handleFormField("stoppedThresh", e.target.value)}
                   required={false}
                  onBlur={() => handleFormFieldBlur("stoppedThresh")}
                  disabled={!isAlertEnabled}
                />
                </div>
              </div>
            )}
           <div
          className={
            isMinimumVisible
              ? "ms-3 d-flex flex-column justify-content-center align-items-start flex-grow-1"
              : " ms-3 d-flex flex-column justify-content-center align-items-start flex-grow-1"
          }
        >
          <div
            className={
              formFields?.step2[stateSelector]?.maximum?.maximum > 9999
                ? "w-100 device-profile-form-slider-with-extra-space"
                : "w-100 device-profile-form-slider"
            }
          >
                  <div ref={sliderRef}>
                  <Slider
                  className="custom-slider"
                  id={stateSelector}
                  value={
                  typeof formState?.stoppedThresh === "object"
                    ? formState?.stoppedThresh?.value
                    : formState?.stoppedThresh
                }
                  min={0}
                  max={16}
                  step={0.1}
                  onChange={(val) => handleFormField("stoppedThresh", val)}
                  tooltip={{
                    open: isInView,
                    placement: "top",
                    autoAdjustOverflow: false,
                    formatter: (value) => value.toFixed(1),
                  }}
                  disabled={!isAlertEnabled}
                  marks={{
                    0: "0",
                    16: "16",
                  }}
                  styles={{
                    track: { backgroundColor: "#008331" },
                    rail: { backgroundColor: "#FF8027" },
                    handle: { background: "#000000", borderColor: "#000000" },
                  }}
                />
                 </div>
              </div>
            </div>
          </div>

            <div className="py-2 w-100 d-flex flex-row justify-content-start align-items-center">
            {isMinimumVisible && (
              <div className="single-max-field d-flex flex-column justify-content-center align-items-start">
                 <span
              className={
                formFields?.step2[stateSelector]?.alert?.value === "no alert"
                  ? "d-flex flex-row justify-content-start align-items-center device-profile-label ats-disabled-text attributes-label"
                  : "d-flex flex-row justify-content-start align-items-center device-profile-label attributes-label"
              }
            >
              Stopped period (min)
            </span>
                 <div className="w-100 pe-2">
                <Input
                  type="number"
                  className={`shipment-form-fields ${formFields?.step2[stateSelector]?.stoppedThresh?.class}`}
                  value={
                  typeof formState?.stoppedPeriod === "object"
                    ? formState?.stoppedPeriod?.value
                    : formState?.stoppedPeriod
                }
                  onChange={(e) => handleFormField("stoppedPeriod", e.target.value)}
                  required={false}
                  onBlur={() => handleFormFieldBlur("stoppedPeriod")}
                  disabled={!isAlertEnabled}
                />
                </div>
              </div>
            )}
           <div
          className={
            isMinimumVisible
              ? "ms-3 d-flex flex-column justify-content-center align-items-start flex-grow-1"
              : " ms-3 d-flex flex-column justify-content-center align-items-start flex-grow-1"
          }
        >
          <div
            className={
              formFields?.step2[stateSelector]?.maximum?.maximum > 9999
                ? "w-100 device-profile-form-slider-with-extra-space"
                : "w-100 device-profile-form-slider"
            }
          >
              <div ref={sliderRef}>
                 <Slider
                  className="custom-slider"
                  id={stateSelector}
                  value={
                  typeof formState?.stoppedPeriod === "object"
                    ? formState?.stoppedPeriod?.value
                    : formState?.stoppedPeriod
                }
                  min={1}
                  max={4320}
                  step={1}
                  onChange={(val) => handleFormField("stoppedPeriod", val)}
                  tooltip={{
                    open: isInView,
                    placement: "top",
                    autoAdjustOverflow: false,
                    formatter: (value) => value,
                  }}
                  disabled={!isAlertEnabled}
                  marks={{
                    1: "1",
                    4320: "4320",
                  }}
                  styles={{
                    track: { backgroundColor: "#008331" },
                    rail: { backgroundColor: "#FF8027" },
                    handle: { background: "#000000", borderColor: "#000000" },
                  }}
                />
                 </div>
              </div>
            </div>
          </div>
          </>
        :""} 
        </>
        } 



          <div className="py-2 w-100 d-flex flex-row justify-content-start align-items-center">
            <div className="w-50 d-flex flex-column justify-content-center align-items-start">
               <span
            className={
               formFields?.step2[stateSelector]?.alert?.value === "no alert"
              ? "d-flex flex-row justify-content-start align-items-center device-profile-label ats-disabled-text attributes-label"
              : "d-flex flex-row justify-content-start align-items-center device-profile-label attributes-label"
            }
          >
            Alert Type{" "}
            <ATSToolTip text="Severity level of the alert">
              <img
                src={
                  formFields?.step2[stateSelector]?.alert?.value ===
                    "no alert" ||
                  (title === "Tilt (deg)" &&
                    (formFields?.step2[stateSelector]?.alert?.value ===
                      "device alert" ||
                      formFields?.step2[stateSelector]?.alert?.value === ""))
                    ? TooltipIIcon
                    : CircleIIcon
                }
                className={
                  formFields?.step2[stateSelector]?.alert?.value ===
                    "no alert" ||
                  (title === "Tilt (deg)" &&
                    (formFields?.step2[stateSelector]?.alert?.value ===
                      "device alert" ||
                      formFields?.step2[stateSelector]?.alert?.value === ""))
                    ? "mx-1 ats-disabled-text"
                    : "mx-1"
                }
                alt="info"
              />
            </ATSToolTip>
          </span>
                <div className="w-100 pe-2">
              <Select
                placeholder="None"
                key={"[stateSelector]-alert-type"}
                className={`shipment-form-fields ${formFields?.step2[stateSelector]?.alertType?.class}`}
                value={formState?.alertType?.value}
                onChange={(val) => handleFormField("alertType", val)}
                options={[
                  { value: "notification", label: "Notification" },
                  { value: "critical", label: "Critical" },
                  { value: "warning", label: "Warning" },
                ]}
                disabled={!isAlertEnabled}
              />
              </div>
            </div>
            <div className="w-50 d-flex flex-column justify-content-center align-items-start">
               <span
            className={
               formFields?.step2[stateSelector]?.alert?.value === "no alert"
              ? "d-flex flex-row justify-content-start align-items-center device-profile-label ats-disabled-text attributes-label"
              : "d-flex flex-row justify-content-start align-items-center device-profile-label attributes-label"
            }
          >
            Alert Group{" "}
            <ATSToolTip
              placement={"topRight"}
              text="Alert groups to be notified of the alert"
            >
              <img
                src={
                  formFields?.step2[stateSelector]?.alert?.value ===
                    "no alert" ||
                  (title === "Tilt (deg)" &&
                    (formFields?.step2[stateSelector]?.alert?.value ===
                      "device alert" ||
                      formFields?.step2[stateSelector]?.alert?.value === ""))
                    ? TooltipIIcon
                    : CircleIIcon
                }
                className={
                  formFields?.step2[stateSelector]?.alert?.value ===
                    "no alert" ||
                  (title === "Tilt (deg)" &&
                    (formFields?.step2[stateSelector]?.alert?.value ===
                      "device alert" ||
                      formFields?.step2[stateSelector]?.alert?.value === ""))
                    ? "mx-1 ats-disabled-text"
                    : "mx-1"
                }
                alt="info"
              />
            </ATSToolTip>
          </span>
              <div className="w-100 pl-2">
              <Select
                mode="multiple"
                className={`shipment-form-fields-alertGroup ${formFields?.step2[stateSelector]?.alertGroup?.class}`}
                allowClear
                placeholder="None"
                value={formState?.alertGroup?.value}
                onChange={(val) => handleFormField("alertGroup", val)}
                options={alertGroupOptions}
                disabled={!isAlertEnabled}
                 style={
                formFields?.step2[stateSelector]?.alertGroup?.value?.length > 0
                  ? {
                      width: "100%",
                    }
                  : {
                      width: "100%",
                      height: "44px",
                    }
              }
              />
              </div>
            </div>
          </div>

          <ScheduleTimeFrame
            initialValues={formState?.schedule}
            onChange={(schedule) => dispatch(updateAttrSchedule(stateSelector, schedule))}
            disabled={!isAlertEnabled}
          />
        </>
      )}
    </div>
  );
}

AttributeFormGroup.propTypes = {
  title: PropTypes.string.isRequired,
  stateSelector: PropTypes.string.isRequired,
  isMinimumVisible: PropTypes.bool.isRequired,
  expandedSection: PropTypes.string,
  setExpandedSection: PropTypes.func,
};

export default AttributeFormGroup;